/**
 * FERP Portal - Feature-Filtered Config Hook
 *
 * Thin wrapper around useTenantSubgraphConfig that applies:
 * 1. DRR-102: Backend-driven scope filtering via allowedNavigation query
 * 2. Feature Catalog layer overrides (additive promotions on top)
 *
 * When scope-filtered data is available from the backend, it replaces
 * the unfiltered nav items/routes. Feature Catalog overrides are then
 * applied on top (they are additive — they promote items, never remove).
 *
 * FERP Principles:
 * - Backend authority: Navigation visibility decided by backend scopes (DRR-102)
 * - Composable: Wraps existing hook, zero changes to useTenantSubgraphConfig
 * - Safe: Falls back to base hook ONLY while allowedNavigation is loading (DRR-102 fail-closed)
 * - Data-driven: Overrides are additive on top of backend metadata
 *
 * Usage:
 * ```tsx
 * // Drop-in replacement for useTenantSubgraphConfig in MainLayout
 * const { loading, getNavItemsForLayer } = useFeatureFilteredConfig(tenantId);
 * ```
 */

import { useCallback, useState, useEffect, useMemo } from 'react';
import {
  useTenantSubgraphConfig,
  groupNavItems,
  type ViewLayer,
  type SubgraphNavItem,
  type SubgraphRoute,
  type TenantSubgraphConfigContext,
  type SettingNavGroup,
} from './useTenantSubgraphConfig';
import type { FeatureLayerOverrides } from './useFeatureCatalog';
import { useViewLayer } from '@/auth/AuthContext';
import { useTenantFeatureFlags, type TenantFeatureFlags } from '@ferp/workflow-forms';
import { useNavigation } from '@/contexts/NavigationContext';
import { ViewLayer as GqlViewLayer } from '@/generated/graphql';

// ============================================================================
// Stable empty array references (prevent infinite re-render loops)
// ============================================================================

// CRITICAL: Using `?? []` creates a NEW array on every render, causing
// useMemo/useCallback dependencies to trigger. Use stable references instead.
const EMPTY_SETTING_GROUPS: SettingNavGroup[] = [];

// ============================================================================
// GQL ViewLayer ('ADMIN') → Config ViewLayer ('Admin') converter
// ============================================================================

const GQL_TO_CONFIG_LAYER: Record<string, ViewLayer> = {
  USER: 'User',
  ADMIN: 'Admin',
  ARCHITECT: 'Architect',
};

function gqlLayerToConfigLayer(gqlLayer: string): ViewLayer {
  return GQL_TO_CONFIG_LAYER[gqlLayer] ?? 'User';
}

// ============================================================================
// localStorage reader (matches useFeatureCatalog storage format)
// ============================================================================

const STORAGE_PREFIX = 'ferp:catalog';

function readOverrides(tenantId: string): FeatureLayerOverrides | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}:${tenantId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FeatureLayerOverrides;
    if (parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

// ============================================================================
// Feature Catalog promotion (extracted for cognitive complexity)
// ============================================================================

function applyPromotions(
  baseItems: SubgraphNavItem[],
  allNavItems: SubgraphNavItem[],
  overrides: FeatureLayerOverrides,
  targetLayer: ViewLayer,
): SubgraphNavItem[] {
  const normalizedLayer = targetLayer.toUpperCase();
  const baseItemPaths = new Set(baseItems.map((item) => item.path));
  const promoted: SubgraphNavItem[] = [];

  for (const item of allNavItems) {
    if ((item.layer as string).toUpperCase() === normalizedLayer) continue;
    if (baseItemPaths.has(item.path)) continue;
    if (!item.path) continue;

    const overrideLayers = overrides.overrides[item.path];
    if (!overrideLayers) continue;

    const isPromoted = overrideLayers.some(
      (l) => l.toUpperCase() === normalizedLayer,
    );

    if (isPromoted) {
      promoted.push({
        ...item,
        layer: targetLayer,
        sortOrder: item.sortOrder + 10000,
      });
    }
  }

  if (promoted.length === 0) return baseItems;
  return [...baseItems, ...promoted];
}

// ============================================================================
// Pattern → Full Path mapping
// ============================================================================

/**
 * Map short pattern names to full paths.
 * FHIR routes use /fhir/, others use /ferp-core/.
 * Sync is a standalone route at /sync.
 */
const PATTERN_TO_PATH: Record<string, string> = {
  // ─────────────────────────────────────────────────────────────────────────
  // FHIR subgraph routes (healthcare-specific)
  // ─────────────────────────────────────────────────────────────────────────
  residents: '/fhir/residents',
  'care-plan': '/fhir/care-plans',
  'care-plans': '/fhir/care-plans',
  assessments: '/fhir/assessments',
  incidents: '/fhir/incidents',
  emar: '/fhir/medication-administration',
  'medication-administration': '/fhir/medication-administration',
  checklist: '/fhir/checklist',
  'witness-queue': '/fhir/witness-queue',
  patients: '/fhir/patients',
  medications: '/fhir/medications',
  observations: '/fhir/observations',
  conditions: '/fhir/conditions',
  allergies: '/fhir/allergies',
  'nursing-notes': '/fhir/nursing-notes',
  'clinical-dashboard': '/fhir/clinical-dashboard',
  'physician-orders': '/fhir/physician-orders',
  'care-tasks': '/fhir/care-tasks',
  'task-assignments': '/ferp-core/task-assignments',
  'overdue-tasks': '/ferp-core/overdue-tasks',
  'task-delegation': '/ferp-core/task-delegation',
  'med-schedule': '/fhir/med-schedule',
  'mar-reports': '/fhir/mar-reports',
  'controlled-substances': '/fhir/controlled-substances',
  'prn-medications': '/fhir/prn-medications',

  // ─────────────────────────────────────────────────────────────────────────
  // Core ferp routes (/ferp-core/ prefix)
  // ─────────────────────────────────────────────────────────────────────────
  dashboard: '/ferp-core/dashboard',
  sync: '/sync',
  inbox: '/ferp-core/inbox',
  tasks: '/ferp-core/tasks',
  documents: '/ferp-core/documents',
  approvals: '/ferp-core/approvals',
  'my-access': '/ferp-core/my-access',
  'my-schedule': '/ferp-core/my-schedule',
  schedule: '/ferp-core/my-schedule',
  calendar: '/ferp-core/calendar',
  shifts: '/ferp-core/shift-plan',
  gantt: '/ferp-core/gantt',
  subjects: '/ferp-core/subjects',
  people: '/ferp-core/people',
  journey: '/ferp-core/journey',
  constellation: '/ferp-core/constellation',
  compliance: '/ferp-core/compliance',
  readiness: '/ferp-core/compliance/readiness',
  training: '/ferp-core/training',
  rulesets: '/ferp-core/rulesets',
  'standards-packs': '/ferp-core/compliance/standards-packs',
  workforce: '/ferp-core/workforce',
  'shift-plan': '/ferp-core/shift-plan',
  team: '/ferp-core/team',
  'staff-list': '/ferp-core/staff-list',
  billing: '/ferp-core/billing',
  claims: '/ferp-core/claims',
  invoice: '/ferp-core/invoice',
  remittance: '/ferp-core/remittance',
  eligibility: '/ferp-core/eligibility',
  'staff-training': '/ferp-core/staff-training',
  'task-groups': '/ferp-core/task-groups',

  // ─────────────────────────────────────────────────────────────────────────
  // Admin routes (/admin/ prefix)
  // ─────────────────────────────────────────────────────────────────────────
  actors: '/admin/actors',  // actual route
  roles: '/admin/roles',  // actual route
  staff: '/ferp-core/workforce',  // no staff route, use workforce
  policies: '/admin/policies',  // actual route
  teams: '/ferp-core/team',  // actual route is /ferp-core/team
  queues: '/admin/queues',  // actual route
  'form-schemas': '/admin/form-schemas',  // actual route
  'entity-types': '/admin/entity-types',  // actual route
  'data-sources': '/ferp-core/data-sources',  // actual route
  intake: '/ferp-core/intake',
  'source-maps': '/admin/source-maps',  // actual route
  'spatial-layout': '/admin/spatial-pinning',  // actual route is spatial-pinning
  'workflow-state': '/ferp-core/instances',  // no workflow-state admin, use instances
  'pdf-studio': '/ferp-core/pdf-studio',  // actual route
  coordination: '/ferp-core/shift-plan',  // no coordination route, use shift-plan
  flowmap: '/ferp-core/flow-map',  // actual route is flow-map
  onboarding: '/ferp-core/onboarding',  // actual route
  // Routes that exist at /ferp-core/ (checked against App.tsx)
  templates: '/ferp-core/form-templates',
  resources: '/ferp-core/resources',
  schedules: '/admin/schedules',  // actual route
  integrations: '/ferp-core/integrations',
  metrics: '/ferp-core/metrics',
  alerts: '/ferp-core/alerts',
  reports: '/ferp-core/reports',
  notifications: '/ferp-core/notifications',
  'safety-events': '/admin/safety-events',  // actual route
  'production-monitoring': '/admin/production-monitoring',  // actual route
  'emergency-participants': '/admin/emergency',  // actual route is /admin/emergency
  audits: '/ferp-core/audits',
  analytics: '/ferp-core/analytics',
  recommendations: '/ferp-core/recommendations',
  reconciliation: '/ferp-core/reconciliation',
  ledger: '/ferp-core/ledger',
  'policy-analytics': '/admin/policy-analytics',  // actual route
  'validation-review': '/admin/validation-review',  // actual route
  'llm-comparison': '/admin/llm-comparison',  // actual route
  'workflow-composition': '/admin/workflow-composition',  // actual route
  'regulatory-governance': '/admin/regulatory-governance',  // actual route
  risks: '/ferp-core/risks',
  evidence: '/ferp-core/evidence',
  rbac: '/admin/rbac',  // actual route
  'role-access': '/admin/role-comparison',  // actual route is role-comparison
  exchange: '/ferp-core/integrations',  // no exchange route, use integrations
  governance: '/ferp-core/governance',
  terminology: '/admin/terminology',  // actual route
  constraints: '/ferp-core/compliance',  // no constraints route, use compliance
  dependencies: '/ferp-core/compliance',  // no dependencies route, use compliance
  contracts: '/ferp-core/contracts',
  validations: '/admin/validations',  // actual route
  events: '/ferp-core/events',
  states: '/ferp-core/states',
  sagas: '/admin/sagas',  // actual route
  checkpoints: '/ferp-core/checkpoints',
  traces: '/ferp-core/traces',
  transactions: '/ferp-core/transactions',
  'regulatory-sources': '/ferp-core/regulatory-sources',
  'coverage-matrix': '/ferp-core/compliance',  // no coverage-matrix route
  'form-schema-overrides': '/ferp-core/form-schema-overrides',
  'form-overrides': '/ferp-core/form-schema-overrides',  // same page
  'task-templates': '/ferp-core/task-templates',
  'work-types': '/ferp-core/task-groups',
  settings: '/ferp-core/settings',
  'tenant-settings': '/ferp-core/settings',  // same page
  branding: '/ferp-core/branding',
  'nav-overrides': '/ferp-core/settings',  // no nav-overrides route
  'briefing-templates': '/ferp-core/briefing-templates',

  // ─────────────────────────────────────────────────────────────────────────
  // Architect routes (/architect/ prefix)
  // ─────────────────────────────────────────────────────────────────────────
  workflows: '/architect/workflows',
  'master-workflow': '/architect/workflows/master',
  'workflow-studio': '/architect/workflows/studio',

  // ─────────────────────────────────────────────────────────────────────────
  // Subgraph routes (admin layer navigation)
  // These patterns appear in nav-grp-admin-subgraphs and need /admin/ prefix
  // to match the Route path="/admin/:subgraphId" → SubgraphBrowserPage
  // ─────────────────────────────────────────────────────────────────────────
  fhir: '/admin/fhir',
  healthcare: '/admin/fhir',
  payroll: '/admin/payroll',
  accounting: '/admin/accounting',
  erp: '/admin/erp',
  edi: '/admin/edi',

  // ─────────────────────────────────────────────────────────────────────────
  // Wearables routes
  // ─────────────────────────────────────────────────────────────────────────
  wearables: '/wearables',
  devices: '/wearables/devices',
  sensors: '/wearables/sensors',
  vitals: '/wearables/vitals',

  // ─────────────────────────────────────────────────────────────────────────
  // AI routes
  // ─────────────────────────────────────────────────────────────────────────
  'ai-agents': '/ai/agents',
  'chat-history': '/ai/chat-history',
  'ai-insights': '/ai/insights',
  summarization: '/ai/summarization',

  // ─────────────────────────────────────────────────────────────────────────
  // Portal routes
  // ─────────────────────────────────────────────────────────────────────────
  'portal-pages': '/portal/pages',
  announcements: '/portal/announcements',
  'family-portal': '/portal/family',
  'resident-portal': '/portal/resident',
};

/**
 * Convert a short pattern to a full path.
 * Patterns starting with "/" are returned as-is (already full paths).
 * Known patterns use the mapping above.
 * Unknown patterns default to /ferp-core/{pattern}.
 */
function patternToPath(pattern: string): string {
  if (pattern.startsWith('/')) return pattern;
  return PATTERN_TO_PATH[pattern] ?? `/ferp-core/${pattern}`;
}

// ============================================================================
// Feature flag → path suppression map
// ============================================================================

/**
 * Paths to suppress when the corresponding tenant feature flag is disabled.
 * Source of truth: `constellation-facility-meta` config `features:` section.
 * Backend reads the config → DDB; `useTenantFeatureFlags` reads DDB via GraphQL.
 *
 * When a flag is false, ALL nav items whose path matches are removed.
 * Fail-open: while flags are loading, DEFAULT_FLAGS = all true, so nothing is hidden.
 */
const FEATURE_FLAG_PATHS: { flag: keyof TenantFeatureFlags; paths: string[] }[] = [
  { flag: 'careJourney', paths: ['/ferp-core/journey'] },
  { flag: 'beaconDashboard', paths: ['/alerts/beacon'] },
  { flag: 'witnessQueue', paths: ['/admin/witness-queue'] },
  { flag: 'standardsPack', paths: ['/ferp-core/compliance/standards-packs'] },
  { flag: 'syncDashboard', paths: ['/sync'] },
  // qapiDashboard is a separate app — no portal nav path to suppress
];

function applyFeatureFlags(
  items: SubgraphNavItem[],
  flags: TenantFeatureFlags,
): SubgraphNavItem[] {
  const suppressedPaths = new Set<string>();
  for (const { flag, paths } of FEATURE_FLAG_PATHS) {
    if (!flags[flag]) {
      for (const p of paths) suppressedPaths.add(p);
    }
  }
  if (suppressedPaths.size === 0) return items;
  return items.filter((item) => !suppressedPaths.has(item.path));
}

// ============================================================================
// Emergency Fallback Navigation
// ============================================================================

/**
 * Emergency fallback navigation when backend queries fail or return empty.
 * Shows essential routes so the app remains navigable during dev/unseeded scenarios.
 * NOT for production - production should have proper nav seeding.
 */
function getEmergencyFallbackNav(layer: ViewLayer): SubgraphNavItem[] {
  const normalizedLayer = layer.toUpperCase();

  // Common items for all layers - Dashboard is top-level (not nested)
  const common: SubgraphNavItem[] = [
    { id: 'fallback-dashboard', label: 'Dashboard', path: '/ferp-core/dashboard', icon: 'IconDashboard', layer, sortOrder: 0, parentId: null, badge: null },
    { id: 'fallback-mywork', label: 'My Work', path: '', icon: 'IconClipboardList', layer, sortOrder: 10, parentId: null, badge: null },
    { id: 'fallback-tasks', label: 'Tasks', path: '/ferp-core/tasks', icon: 'IconChecklist', layer, sortOrder: 11, parentId: 'fallback-mywork', badge: null },
    { id: 'fallback-inbox', label: 'Inbox', path: '/ferp-core/inbox', icon: 'IconInbox', layer, sortOrder: 12, parentId: 'fallback-mywork', badge: null },
    { id: 'fallback-healthcare', label: 'Healthcare', path: '', icon: 'IconHeartbeat', layer, sortOrder: 15, parentId: null, badge: null },
    { id: 'fallback-residents', label: 'Residents', path: '/fhir/residents', icon: 'IconUsers', layer, sortOrder: 16, parentId: 'fallback-healthcare', badge: null },
    { id: 'fallback-careplans', label: 'Care Plans', path: '/fhir/care-plans', icon: 'IconClipboardList', layer, sortOrder: 17, parentId: 'fallback-healthcare', badge: null },
    { id: 'fallback-observations', label: 'Observations', path: '/fhir/observations', icon: 'IconTestPipe', layer, sortOrder: 18, parentId: 'fallback-healthcare', badge: null },
    { id: 'fallback-schedule', label: 'Schedule', path: '', icon: 'IconCalendarEvent', layer, sortOrder: 20, parentId: null, badge: null },
    { id: 'fallback-my-schedule', label: 'My Schedule', path: '/ferp-core/my-schedule', icon: 'IconCalendar', layer, sortOrder: 21, parentId: 'fallback-schedule', badge: null },
    { id: 'fallback-calendar', label: 'Calendar', path: '/ferp-core/calendar', icon: 'IconCalendarStats', layer, sortOrder: 22, parentId: 'fallback-schedule', badge: null },
    { id: 'fallback-wearables', label: 'Wearables', path: '', icon: 'IconDeviceWatch', layer, sortOrder: 30, parentId: null, badge: null },
    { id: 'fallback-alerts', label: 'Alerts', path: '/wearables/alerts', icon: 'IconBell', layer, sortOrder: 31, parentId: 'fallback-wearables', badge: null },
    { id: 'fallback-devices', label: 'Devices', path: '/wearables/devices', icon: 'IconDevices', layer, sortOrder: 32, parentId: 'fallback-wearables', badge: null },
    { id: 'fallback-team', label: 'Team', path: '', icon: 'IconUsers', layer, sortOrder: 40, parentId: null, badge: null },
    { id: 'fallback-workforce', label: 'Workforce', path: '/ferp-core/workforce', icon: 'IconUserCircle', layer, sortOrder: 41, parentId: 'fallback-team', badge: null },
    { id: 'fallback-shift-plan', label: 'Shift Plan', path: '/ferp-core/coordination/shift-plan', icon: 'IconClock', layer, sortOrder: 42, parentId: 'fallback-team', badge: null },
    { id: 'fallback-training', label: 'Training', path: '/ferp-core/training', icon: 'IconSchool', layer, sortOrder: 50, parentId: null, badge: null },
  ];

  if (normalizedLayer === 'USER') {
    return common;
  }

  // Admin/Architect get more items
  const adminItems: SubgraphNavItem[] = [
    { id: 'fallback-operations', label: 'Operations', path: '', icon: 'IconSettings', layer, sortOrder: 10, parentId: null, badge: null },
    { id: 'fallback-workflows', label: 'Workflows', path: '/ferp-core/workflows', icon: 'IconGitBranch', layer, sortOrder: 11, parentId: 'fallback-operations', badge: null },
    { id: 'fallback-instances', label: 'Instances', path: '/ferp-core/instances', icon: 'IconPlayerPlay', layer, sortOrder: 12, parentId: 'fallback-operations', badge: null },
    { id: 'fallback-actors', label: 'Actors', path: '/ferp-core/actors', icon: 'IconUsers', layer, sortOrder: 13, parentId: 'fallback-operations', badge: null },
    { id: 'fallback-policies', label: 'Policies', path: '/ferp-core/policies', icon: 'IconScale', layer, sortOrder: 14, parentId: 'fallback-operations', badge: null },
  ];

  if (normalizedLayer === 'ARCHITECT') {
    const architectItems: SubgraphNavItem[] = [
      { id: 'fallback-architect', label: 'Architect Tools', path: '', icon: 'IconTools', layer, sortOrder: 20, parentId: null, badge: null },
      { id: 'fallback-primitives', label: 'Primitives', path: '/architect/primitives', icon: 'IconBox', layer, sortOrder: 21, parentId: 'fallback-architect', badge: null },
      { id: 'fallback-registries', label: 'Registries', path: '/architect/registries', icon: 'IconDatabase', layer, sortOrder: 22, parentId: 'fallback-architect', badge: null },
      { id: 'fallback-dev', label: 'Dev Tools', path: '/dev', icon: 'IconCode', layer, sortOrder: 23, parentId: 'fallback-architect', badge: null },
    ];
    return [...common, ...adminItems, ...architectItems];
  }

  return [...common, ...adminItems];
}

// ============================================================================
// Nav Item Deduplication (extracted for cognitive complexity)
// ============================================================================

/**
 * Deduplicate nav items by label+path while preserving children.
 * When duplicate parents are removed, their children are reparented to the kept parent.
 * Items with same label but different paths are distinct (not duplicates).
 */
function deduplicateNavItems(items: SubgraphNavItem[]): SubgraphNavItem[] {
  const topLevelLabelToKeptId = new Map<string, string>();
  const seenByParent = new Map<string, Set<string>>();
  const parentLabelToKeptId = new Map<string, Map<string, string>>();
  const keptItems: SubgraphNavItem[] = [];
  const removedParentIds = new Set<string>();

  // Dedup key: label + path. Same label with different paths = distinct items.
  const dedupKey = (item: SubgraphNavItem) => `${item.label}::${item.path}`;

  // First pass: identify duplicates and track kept IDs
  for (const item of items) {
    if (item.parentId === null) {
      if (topLevelLabelToKeptId.has(item.label)) {
        removedParentIds.add(item.id);
        continue;
      }
      topLevelLabelToKeptId.set(item.label, item.id);
      keptItems.push(item);
    } else {
      const parentKey = item.parentId;
      if (!seenByParent.has(parentKey)) {
        seenByParent.set(parentKey, new Set());
        parentLabelToKeptId.set(parentKey, new Map());
      }
      const parentSet = seenByParent.get(parentKey)!;
      const keptMap = parentLabelToKeptId.get(parentKey)!;
      const key = dedupKey(item);

      if (parentSet.has(key)) {
        removedParentIds.add(item.id);
        continue;
      }
      parentSet.add(key);
      keptMap.set(item.label, item.id);
      keptItems.push(item);
    }
  }

  // Second pass: reparent children of removed parents
  const reparentedItems = keptItems.map(item => {
    if (!item.parentId || !removedParentIds.has(item.parentId)) return item;
    const removedParent = items.find(i => i.id === item.parentId);
    if (!removedParent) return item;

    const keptParentId = removedParent.parentId === null
      ? topLevelLabelToKeptId.get(removedParent.label)
      : parentLabelToKeptId.get(removedParent.parentId)?.get(removedParent.label);

    return keptParentId ? { ...item, parentId: keptParentId } : item;
  });

  // Third pass: deduplicate children after reparenting (by label+path)
  const finalSeenByParent = new Map<string, Set<string>>();
  return reparentedItems.filter(item => {
    if (item.parentId === null) return true;
    const parentKey = item.parentId;
    if (!finalSeenByParent.has(parentKey)) {
      finalSeenByParent.set(parentKey, new Set());
    }
    const parentSet = finalSeenByParent.get(parentKey)!;
    const key = dedupKey(item);
    if (parentSet.has(key)) return false;
    parentSet.add(key);
    return true;
  });
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Feature-filtered config hook. Drop-in replacement for useTenantSubgraphConfig
 * in consumers that need to respect Feature Catalog overrides.
 *
 * With no overrides, returns identical results to useTenantSubgraphConfig.
 */
export function useFeatureFilteredConfig(tenantId: string | null): TenantSubgraphConfigContext {
  const base = useTenantSubgraphConfig(tenantId);
  const jwtViewLayer = useViewLayer(); // 'user' | 'admin' | 'architect' (from JWT roles)
  const { flags: featureFlags } = useTenantFeatureFlags();

  // DRR-313: Use NavigationContext to avoid duplicate queries
  const {
    allowedNavigation,
    settingBundle,
    userScopes,
    loading: navLoading,
  } = useNavigation();

  // gqlLayer for layer filtering (derived from JWT)
  const gqlLayer = jwtViewLayer.toUpperCase() as GqlViewLayer;

  // CRITICAL: Use stable empty array reference to avoid infinite re-renders
  // settingBundle?.navigationGroups ?? [] creates a NEW array on every render
  const rawSettingGroups: SettingNavGroup[] = settingBundle?.navigationGroups ?? EMPTY_SETTING_GROUPS;

  const settingGroups = useMemo(() => {
    // DRR-102: While scopes are loading, don't filter by requiredScopes.
    // This prevents nav groups from disappearing during user switch when
    // allowedNavigation query is refetching. Once scopes load, filtering kicks in.
    // NOTE: navLoading=true means scopes are still being fetched.
    if (navLoading && userScopes.size === 0) {
      // Scopes loading — show all groups (no filtering yet)
      return rawSettingGroups;
    }

    const hasWildcard = userScopes.has('*') || userScopes.has('*:*');
    return rawSettingGroups.filter((group) => {
      // No required scopes = visible to all
      if (!group.requiredScopes?.length) return true;
      // Wildcard scope = see everything
      if (hasWildcard) return true;
      // Check if user has any of the required scopes
      return group.requiredScopes.some((scope) => userScopes.has(scope));
    });
  }, [rawSettingGroups, userScopes, navLoading]);

  // Read overrides from localStorage (re-read when tenantId changes)
  const [overrides, setOverrides] = useState<FeatureLayerOverrides | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setOverrides(null);
      return;
    }
    setOverrides(readOverrides(tenantId));

    // Listen for storage events (cross-tab sync + same-tab updates from useFeatureCatalog)
    const handler = (e: StorageEvent) => {
      if (e.key === `${STORAGE_PREFIX}:${tenantId}`) {
        setOverrides(readOverrides(tenantId));
      }
    };
    window.addEventListener('storage', handler);

    // Also poll on interval for same-tab updates (StorageEvent doesn't fire in same tab)
    // Use functional update to only trigger re-render if value actually changed
    const interval = setInterval(() => {
      const newOverrides = readOverrides(tenantId);
      setOverrides((prev) => {
        // Compare JSON strings to avoid re-render on identical objects
        if (JSON.stringify(prev) === JSON.stringify(newOverrides)) return prev;
        return newOverrides;
      });
    }, 1000);

    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, [tenantId]);

  // Override getNavItemsForLayer to use scope-filtered data (DRR-102)
  // then apply Feature Catalog promotions on top
  const getNavItemsForLayer = useCallback(
    (layer: ViewLayer): SubgraphNavItem[] => {
      const isActiveLayer = layer.toUpperCase() === gqlLayer;

      // DRR-102: Use scope-filtered items for the active view layer.
      // Fail-closed: if backend returns empty navItems, show NOTHING.
      // Only fall back to base config data while allowedNavigation query is still loading.
      let baseItems: SubgraphNavItem[];
      const backendNav = allowedNavigation;

      if (isActiveLayer && backendNav) {
        // Backend responded — use its filtered items (may be empty = no access)
        // DRR-188: Prefer settingGroups (from settingBundle YAML) over SubgraphRegistry navItems.
        // settingBundle is the authoritative source for nav configuration — it includes
        // role-specific nav groups like Schedule, Team, Wearables that SubgraphRegistry
        // may not have seeded. SubgraphRegistry navItems are legacy/transitional.
        if (settingGroups.length > 0) {
          // DRR-102 fallback: allowedNavigation empty but settingBundle has groups.
          // This happens when SubgraphRegistry isn't seeded (local dev).
          // Use settingGroups directly with the group's items.
          // DRR-196: Filter items by memberItemScopes (item-level scope requirements).
          const itemScopeCheck = (pattern: string, itemScopes: Record<string, string[]> | null | undefined) => {
            if (!itemScopes || !itemScopes[pattern]) return true; // No scope required
            const requiredScopes = itemScopes[pattern];
            // Check if user has any of the required scopes
            return requiredScopes.some((scope) => userScopes.has(scope));
          };
          // DRR-335: Use layer hierarchy — higher layers see all lower layers
          // ARCHITECT sees: ARCHITECT, ADMIN, USER groups
          // ADMIN sees: ADMIN, USER groups
          // USER sees: USER groups only
          const layerIncludes = (groupLayer: string): boolean => {
            const gl = groupLayer.toUpperCase();
            switch (gqlLayer) {
              case 'ARCHITECT': return gl === 'ARCHITECT' || gl === 'ADMIN' || gl === 'USER';
              case 'ADMIN': return gl === 'ADMIN' || gl === 'USER';
              default: return gl === 'USER';
            }
          };
          baseItems = settingGroups
            .filter((g) => layerIncludes(g.layer))
            .flatMap((g) => {
              // Parse memberItemScopes - it's a JSON object from GraphQL
              const itemScopes = g.memberItemScopes as Record<string, string[]> | null;
              return [
                { id: g.id, label: g.label, path: g.primaryPath ?? '', icon: g.icon ?? '', layer: gqlLayerToConfigLayer(g.layer), sortOrder: g.sortOrder, parentId: null, badge: null },
                ...(g.memberPatterns ?? [])
                  .filter((pattern) => itemScopeCheck(pattern, itemScopes))
                  .map((pattern, i) => {
                    // DRR-196: member_patterns are short names like "tasks", "inbox".
                    // Convert to full paths for routing using PATTERN_TO_PATH mapping.
                    const fullPath = patternToPath(pattern);
                    return {
                      id: `${g.id}-item-${i}`,
                      label: pattern.split('/').pop()?.replace(/-/g, ' ') ?? pattern,
                      path: fullPath,
                      icon: '',
                      layer: gqlLayerToConfigLayer(g.layer),
                      sortOrder: g.sortOrder + i + 1,
                      parentId: g.id,
                      badge: null,
                    };
                  }),
              ];
            });
        } else {
          // DRR-102: Backend returned empty nav AND no settingGroups fallback.
          // Emergency fallback: show essential navigation for dev/unseed scenarios.
          console.warn('[useFeatureFilteredConfig] No nav data from backend — using emergency fallback');
          baseItems = getEmergencyFallbackNav(layer);
        }
      } else if (navLoading || !tenantId) {
        // DRR-123: RESTRICTIVE default — show nothing while scopes are loading.
        // Prevents flashing unfiltered nav items (e.g., DON seeing Claims briefly).
        // Also show nothing when tenantId is null (auth still restoring) — Apollo
        // treats skipped queries as not loading, but we should wait for auth.
        baseItems = [];
      } else {
        // Query ran (tenantId exists) but returned nothing AND no settingGroups fallback.
        // Use emergency fallback for dev/unseed scenarios.
        console.warn('[useFeatureFilteredConfig] Nav query returned empty — using emergency fallback');
        baseItems = getEmergencyFallbackNav(layer);
      }

      // Inject frontend-only nav items that the backend may not have seeded yet.
      // DRR-102: Only inject if user has the required scope (fail-closed).
      // These items should migrate to backend seeding — frontend injection is transitional.
      const normalizedLayer = layer.toUpperCase();
      const scopeSet = new Set(backendNav?.userScopes ?? []);
      const hasScope = (scope: string) => scopeSet.has('*') || scopeSet.has('*:*') || scopeSet.has(scope);

      // RBAC pages — inject into Governance group if user has facility:admin scope.
      if ((normalizedLayer === 'ADMIN' || normalizedLayer === 'ARCHITECT') && hasScope('facility:admin')) {
        const govGroup = baseItems.find(i =>
          !i.parentId && (i.label === 'Governance' || i.id?.includes('governance')),
        );
        if (govGroup) {
          const injections: SubgraphNavItem[] = [];

          // RBAC Dashboard — consolidated role management
          if (!baseItems.some(i => i.path === '/admin/rbac')) {
            injections.push({
              id: normalizedLayer === 'ADMIN' ? 'nav-rbac' : 'nav-rbac-arch',
              label: 'RBAC Dashboard',
              path: '/admin/rbac',
              icon: 'IconShieldLock',
              layer: normalizedLayer === 'ADMIN' ? 'Admin' : 'Architect',
              sortOrder: govGroup.sortOrder + 1,
              parentId: govGroup.id,
              badge: null,
            });
          }

          // Role Access Comparison (DRR-103)
          if (!baseItems.some(i => i.path === '/admin/role-access')) {
            injections.push({
              id: normalizedLayer === 'ADMIN' ? 'nav-role-access' : 'nav-role-access-arch',
              label: 'Role Access',
              path: '/admin/role-access',
              icon: 'IconScale',
              layer: normalizedLayer === 'ADMIN' ? 'Admin' : 'Architect',
              sortOrder: govGroup.sortOrder + 2,
              parentId: govGroup.id,
              badge: null,
            });
          }

          if (injections.length > 0) {
            baseItems = [...baseItems, ...injections];
          }
        }
      }

      // My Access — visible to ALL layers (DRR-102 transparency).
      // Users should know their own scope/role/lens visibility regardless of layer.
      // Backend scopes gate the actual data; this just shows "what can I see?"
      if (!baseItems.some(i => i.path === '/my-access')) {
        // Find any group to attach to; prefer My Work for USER, Governance for ADMIN/ARCHITECT
        const parentGroup = normalizedLayer === 'USER'
          ? baseItems.find(i => !i.parentId && (i.label === 'My Work' || i.id?.includes('mywork')))
          : baseItems.find(i => !i.parentId && (i.label === 'Governance' || i.id?.includes('governance')));
        if (parentGroup) {
          baseItems = [...baseItems, {
            id: `nav-my-access-${normalizedLayer.toLowerCase()}`,
            label: 'My Access',
            path: '/my-access',
            icon: 'IconUserCheck',
            layer,
            sortOrder: parentGroup.sortOrder + 99,
            parentId: parentGroup.id,
            badge: null,
          }];
        }
      }

      // Feature Catalog overrides: promote items from other layers (additive only)
      const withPromotions = (!overrides || !base.config)
        ? baseItems
        : applyPromotions(baseItems, base.config.allNavItems, overrides, layer);

      // Tenant feature flags: suppress nav items for disabled features (DRR-055)
      const filteredItems = applyFeatureFlags(withPromotions, featureFlags);

      // Deduplicate nav items while preserving children (extracted to helper for complexity)
      const finalItems = deduplicateNavItems(filteredItems);

      return finalItems;
    },
    [base, overrides, allowedNavigation, gqlLayer, settingGroups, featureFlags],
  );

  // DRR-102: Override getRoutesForLayer to use scope-filtered routes.
  // Fail-closed: empty backend routes = no route access.
  // Fall back to base config data only while query is still loading.
  const getRoutesForLayer = useCallback(
    (layer: ViewLayer): SubgraphRoute[] => {
      const isActiveLayer = layer.toUpperCase() === gqlLayer;
      const backendNav = allowedNavigation;
      if (isActiveLayer && backendNav) {
        // Backend responded — use its filtered routes (may be empty = no access)
        if (backendNav.routes.length > 0) {
          return backendNav.routes.map((route) => ({
            path: route.path,
            component: route.component,
            layer: gqlLayerToConfigLayer(route.layer),
            title: route.title,
            requiredPermissions: route.requiredPermissions,
            requiresLifeCriticalAuth: route.requiresLifeCriticalAuth,
          }));
        }
        return []; // DRR-102: Backend returned empty = no route access
      }
      // DRR-123: RESTRICTIVE default — no routes while scopes are loading (fail-closed).
      return [];
    },
    [allowedNavigation, gqlLayer],
  );

  return {
    ...base,
    // DRR-123: loading = true until BOTH config AND scope query resolve.
    // Also loading when tenantId is null (auth still restoring) — Apollo treats
    // skipped queries as not loading, but we need to wait for auth.
    // MainLayout shows skeleton nav during this time (restrictive default).
    loading: base.loading || navLoading || !tenantId,
    getNavItemsForLayer,
    getRoutesForLayer,
  };
}

export default useFeatureFilteredConfig;
